<template>
  <div
    class="absolute z-10"
    :style="{ top: `${top * 0.25}rem`, right: `${right * 0.25}rem` }"
  >
    <UButton
      v-if="showFavoriteButton"
      :aria-label="t('a11y.favorite-button')"
      v-bind="
        isFavorite
          ? {
              variant: 'solid',
              color: 'error',
              icon: 'i-heroicons-heart-20-solid',
            }
          : { variant: 'subtle', color: 'neutral', icon: 'i-heroicons-heart' }
      "
      :size="buttonSize"
      @click.prevent="handleFavoriteButton()"
    />
  </div>

  <AuthModal
    v-model="modalIsOpen"
    :modaltitle="t('views.cottage-list.cottageCard.favorite')"
  >
    <template #default>
      <CottageBookmarks
        :item="item"
        @submit-success="closeModal"
      ></CottageBookmarks>
    </template>
  </AuthModal>
</template>

<script setup lang="ts">
import { browserFetch as fetchBookmarkDelete } from '~~/modules/api/rurality/v3/bookmarks/delete'
import { useUserStore } from '~~/modules/auth/stores/userStore'
import { sendConnectifSaveFavoriteEvent } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

const userStore = useUserStore()

const modalIsOpen = ref(false)
const showFavoriteButton = ref<boolean>(false)
const isFavorite = ref<boolean>(false)

type itemType = {
  id: string
  name: string
  province: {
    slug: string
    name: string
    url: string
  }
  village: {
    slug: string
    name: string
  }
  media: Array<{
    url: string
    type: 'PHOTO' | 'VIDEO'
    alt: string
    width: number | null
    height: number | null
  }>
}

interface propsInterface {
  item: itemType
  top?: number
  right?: number
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

const props = withDefaults(defineProps<propsInterface>(), {
  top: 4,
  right: 4,
  buttonSize: 'md',
})

const closeModal = async () => {
  modalIsOpen.value = false
}

const handleFavoriteButton = async () => {
  if (isFavorite.value) {
    const data = await fetchBookmarkDelete({
      cottageUid: props.item.id,
    })
    if (data.success) {
      isFavorite.value = false
    }
  } else {
    modalIsOpen.value = true
    sendConnectifSaveFavoriteEvent()
  }
}

const init = async () => {
  const user = userStore.getUserdata.value
  if (!user.logged) {
    showFavoriteButton.value = true
  } else if (user.logged && user.params?.cottage_bookmarks) {
    showFavoriteButton.value = true
    const cottageBookmarks = user.params?.cottage_bookmarks || []
    isFavorite.value = cottageBookmarks.some(
      (bookmark: { cottageId: string }) => bookmark.cottageId === props.item.id,
    )
  }
}

const unsubscribe = watch(
  () => userStore.state.value.logged,
  () => {
    init()
  },
)
onUnmounted(() => {
  unsubscribe()
})

watchEffect(() => {
  const cottageBookmarks =
    userStore.getUserdata.value.params?.cottage_bookmarks || []
  isFavorite.value = cottageBookmarks.some(
    (bookmark: { cottageId: string }) => bookmark.cottageId === props.item.id,
  )
})

onMounted(() => {
  init()
})
</script>
